import { useEffect, useState, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { toast } from "sonner";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { MapPin, Navigation, Loader2, Phone, Building2, RefreshCcw, Route } from "lucide-react";

// Leaflet's default icon uses relative paths that break under bundlers; override.
const iconFor = (color) =>
  L.divIcon({
    className: "",
    html: `<div style="width:22px;height:22px;border-radius:50%;background:${color};border:3px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.3)"></div>`,
    iconSize: [22, 22],
    iconAnchor: [11, 11],
  });
const HERE_ICON = L.divIcon({
  className: "",
  html: `<div style="width:16px;height:16px;border-radius:50%;background:#003399;border:4px solid #fff;box-shadow:0 0 0 4px rgba(0,51,153,0.25)"></div>`,
  iconSize: [16, 16],
  iconAnchor: [8, 8],
});
const PRIORITY_COLOR = { high: "#dc2626", medium: "#f59e0b", low: "#64748b" };

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return;
    const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lng]));
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 12 });
  }, [points, map]);
  return null;
}

export default function NearbyMapPanel() {
  const [origin, setOrigin] = useState(null); // {lat,lng} or {city,state}
  const [radius, setRadius] = useState(25);
  const [clients, setClients] = useState([]);
  const [busy, setBusy] = useState(false);
  const [geoBusy, setGeoBusy] = useState(false);

  const fetchNearby = async (originOverride = origin) => {
    setBusy(true);
    try {
      const params = { radius_miles: radius };
      if (originOverride?.lat) { params.lat = originOverride.lat; params.lng = originOverride.lng; }
      const { data } = await api.get("/sales/nearby", { params });
      setClients(data.clients || []);
      if (data.origin?.lat) setOrigin(data.origin);
    } catch (err) { toast.error("Could not load nearby clients"); }
    finally { setBusy(false); }
  };

  useEffect(() => { fetchNearby(); /* eslint-disable-next-line */ }, [radius]);

  const useMyLocation = () => {
    if (!navigator.geolocation) return toast.error("Geolocation not supported");
    setGeoBusy(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const o = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setOrigin(o);
        setGeoBusy(false);
        fetchNearby(o);
      },
      (err) => { setGeoBusy(false); toast.error(err.message || "Location permission denied"); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const geocodeMissing = async () => {
    const { data: all } = await api.get("/sales/clients");
    const missing = all.filter((c) => c.lat == null || c.lng == null);
    if (missing.length === 0) return toast.success("Every client already has coordinates");
    toast.info(`Geocoding ${missing.length} client(s)…`);
    let ok = 0;
    for (const c of missing) {
      try { await api.post(`/sales/clients/${c.id}/geocode`); ok++; } catch (_) {}
    }
    toast.success(`Geocoded ${ok} of ${missing.length}`);
    fetchNearby();
  };

  const points = useMemo(
    () => clients.filter((c) => c.lat != null && c.lng != null).map((c) => ({ ...c, lat: Number(c.lat), lng: Number(c.lng) })),
    [clients],
  );
  const mapCenter = origin?.lat ? [origin.lat, origin.lng] : (points[0] ? [points[0].lat, points[0].lng] : [39.5, -98.35]);

  const planRoute = () => {
    // Google Maps directions with up to 5 waypoints (top nearest, mapped from `points`).
    // Works on desktop and hands off to the Google Maps / Apple Maps native app on iOS.
    const stops = points.slice(0, 5);
    if (stops.length === 0) return toast.error("No mapped clients to route to");
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    if (isIOS) {
      // Apple Maps: origin → daddr with '+to:' between stops
      const daddr = stops.map((s) => `${s.lat},${s.lng}`).join("+to:");
      const url = origin?.lat
        ? `http://maps.apple.com/?saddr=${origin.lat},${origin.lng}&daddr=${daddr}`
        : `http://maps.apple.com/?daddr=${daddr}`;
      window.open(url, "_blank");
      return;
    }
    const destination = `${stops[stops.length - 1].lat},${stops[stops.length - 1].lng}`;
    const waypoints = stops.slice(0, -1).map((s) => `${s.lat},${s.lng}`).join("|");
    const params = new URLSearchParams({ api: "1", destination });
    if (origin?.lat) params.set("origin", `${origin.lat},${origin.lng}`);
    if (waypoints) params.set("waypoints", waypoints);
    window.open(`https://www.google.com/maps/dir/?${params.toString()}`, "_blank");
    toast.success(`Route with ${stops.length} stop${stops.length === 1 ? "" : "s"} opened in Maps`);
  };

  return (
    <div className="border border-slate-200 rounded-md bg-white shadow-sm overflow-hidden" data-testid="nearby-panel">
      <div className="flex flex-wrap items-center gap-2 p-3 border-b border-slate-200">
        <Button onClick={useMyLocation} disabled={geoBusy} data-testid="use-my-location-btn" className="bg-blue-800 hover:bg-blue-900 text-white rounded-sm h-9">
          {geoBusy ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Navigation size={14} className="mr-1.5" />}
          Near me
        </Button>
        <Select value={String(radius)} onValueChange={(v) => setRadius(Number(v))}>
          <SelectTrigger data-testid="radius-select" className="w-36 rounded-sm h-9"><SelectValue /></SelectTrigger>
          <SelectContent>
            {[5, 10, 25, 50, 100].map((r) => (<SelectItem key={r} value={String(r)}>{r} miles</SelectItem>))}
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={geocodeMissing} data-testid="geocode-missing-btn" className="rounded-sm h-9 border-slate-300">
          <RefreshCcw size={14} className="mr-1.5" /> Geocode addresses
        </Button>
        <Button variant="outline" onClick={planRoute} data-testid="plan-route-btn" className="rounded-sm h-9 border-blue-300 text-blue-800 hover:bg-blue-50">
          <Route size={14} className="mr-1.5" /> Plan a route
        </Button>
        <div className="ml-auto text-xs font-mono text-slate-500">
          {busy ? "Loading…" : `${points.length} of ${clients.length} on map`}
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-5">
        <div className="lg:col-span-3 h-[420px]" data-testid="nearby-map">
          <MapContainer center={mapCenter} zoom={7} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{y}/{x}.png"
            />
            {origin?.lat && (
              <>
                <Marker position={[origin.lat, origin.lng]} icon={HERE_ICON}><Popup>You are here</Popup></Marker>
                <Circle center={[origin.lat, origin.lng]} radius={radius * 1609.34} pathOptions={{ color: "#003399", fillOpacity: 0.05 }} />
              </>
            )}
            {points.map((c) => (
              <Marker key={c.id} position={[c.lat, c.lng]} icon={iconFor(PRIORITY_COLOR[c.priority] || "#64748b")}>
                <Popup>
                  <div className="text-sm">
                    <div className="font-bold">{c.company_name}</div>
                    {c.contact_name && <div className="text-xs text-slate-600">{c.contact_name}</div>}
                    {c.phone && <div className="text-xs font-mono">{c.phone}</div>}
                    <Link to={`/sales/clients/${c.id}`} className="text-xs text-blue-800 font-semibold hover:underline">Open profile →</Link>
                  </div>
                </Popup>
              </Marker>
            ))}
            <FitBounds points={origin?.lat ? [{ lat: origin.lat, lng: origin.lng }, ...points] : points} />
          </MapContainer>
        </div>
        <div className="lg:col-span-2 max-h-[420px] overflow-y-auto border-t lg:border-t-0 lg:border-l border-slate-200">
          {clients.length === 0 ? (
            <div className="p-6 text-center text-sm text-slate-500">
              {origin?.lat ? "No clients within this radius yet." : "Tap \"Near me\" or pick a client's city to see nearby matches."}
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {clients.map((c) => (
                <li key={c.id} className="p-3" data-testid={`nearby-row-${c.id}`}>
                  <Link to={`/sales/clients/${c.id}`} className="font-semibold text-slate-900 hover:text-blue-800">{c.company_name}</Link>
                  <div className="mt-0.5 text-xs text-slate-500 flex flex-wrap gap-x-3">
                    {c.city && <span><MapPin size={11} className="inline mr-0.5" />{c.city}, {c.state}</span>}
                    {c.phone && <span><Phone size={11} className="inline mr-0.5" />{c.phone}</span>}
                    {c.distance_miles != null && <span className="font-mono font-semibold text-slate-800">{c.distance_miles} mi</span>}
                    {c.lat == null && <span className="text-amber-600">no coords</span>}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
